import { useEffect, useRef } from 'react'
import * as Cesium from 'cesium'
import 'cesium/Build/Cesium/Widgets/widgets.css'

type ThreatPoint = {
  lat: number
  lon: number
  color: string
  scale?: number
}

const THREATS: ThreatPoint[] = [
  { lat: 38.9, lon: -77.0, color: '#ff5a4f', scale: 1.1 },
  { lat: 51.5, lon: -0.1, color: '#ffb347', scale: 0.9 },
  { lat: 32.1, lon: 34.8, color: '#ff5a4f', scale: 1.05 },
  { lat: 25.2, lon: 55.3, color: '#ff9f43', scale: 0.85 },
  { lat: 1.3, lon: 103.8, color: '#3ecbff', scale: 0.8 },
  { lat: 35.7, lon: 139.7, color: '#ff5a4f', scale: 1 },
  { lat: -33.9, lon: 18.4, color: '#72ffd2', scale: 0.75 },
]

function makeThreatMarker(fillHex: string, size = 42): string {
  const canvas = document.createElement('canvas')
  canvas.width = size
  canvas.height = size
  const ctx = canvas.getContext('2d')
  if (!ctx) return ''

  const cx = size / 2
  const cy = size / 2

  const glow = ctx.createRadialGradient(cx, cy, 1, cx, cy, size * 0.46)
  glow.addColorStop(0, `${fillHex}ee`)
  glow.addColorStop(0.38, `${fillHex}66`)
  glow.addColorStop(1, `${fillHex}00`)
  ctx.fillStyle = glow
  ctx.beginPath()
  ctx.arc(cx, cy, size * 0.46, 0, Math.PI * 2)
  ctx.fill()

  ctx.fillStyle = fillHex
  ctx.beginPath()
  ctx.arc(cx, cy, size * 0.12, 0, Math.PI * 2)
  ctx.fill()

  ctx.strokeStyle = 'rgba(255,255,255,0.55)'
  ctx.lineWidth = 1.5
  ctx.beginPath()
  ctx.arc(cx, cy, size * 0.22, 0, Math.PI * 2)
  ctx.stroke()

  return canvas.toDataURL('image/png')
}

export default function LandingHeroGlobe() {
  const hostRef = useRef<HTMLDivElement | null>(null)
  const viewerRef = useRef<Cesium.Viewer | null>(null)

  useEffect(() => {
    if (!hostRef.current) return

    const viewer = new Cesium.Viewer(hostRef.current, {
      animation: false,
      baseLayerPicker: false,
      fullscreenButton: false,
      geocoder: false,
      homeButton: false,
      infoBox: false,
      navigationHelpButton: false,
      timeline: false,
      sceneModePicker: false,
      selectionIndicator: false,
      skyAtmosphere: false,
      skyBox: false,
      shadows: false,
      shouldAnimate: true,
      terrain: Cesium.Terrain.fromWorldTerrain(),
    })

    viewerRef.current = viewer

    const { scene, camera, entities, imageryLayers } = viewer

    ;(viewer.cesiumWidget.creditContainer as HTMLElement).style.display = 'none'
    scene.globe.showGroundAtmosphere = true
    scene.globe.enableLighting = true
    scene.globe.dynamicAtmosphereLighting = true
    scene.globe.dynamicAtmosphereLightingFromSun = true
    scene.globe.atmosphereBrightnessShift = -0.15
    scene.globe.atmosphereHueShift = -0.02
    scene.globe.atmosphereSaturationShift = -0.1
    scene.globe.baseColor = Cesium.Color.fromCssColorString('#09111c')
    scene.backgroundColor = Cesium.Color.fromCssColorString('#05080d')
    scene.fog.enabled = false
    scene.postProcessStages.fxaa.enabled = true
    scene.screenSpaceCameraController.enableRotate = false
    scene.screenSpaceCameraController.enableTranslate = false
    scene.screenSpaceCameraController.enableZoom = false
    scene.screenSpaceCameraController.enableTilt = false
    scene.screenSpaceCameraController.enableLook = false

    imageryLayers.removeAll()
    imageryLayers.addImageryProvider(
      awaitableProvider(new Cesium.UrlTemplateImageryProvider({
        url: 'https://services.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
        credit: 'Esri',
      })),
    )

    viewer.scene.globe.maximumScreenSpaceError = 1.5

    THREATS.forEach((threat) => {
      entities.add({
        position: Cesium.Cartesian3.fromDegrees(threat.lon, threat.lat, 120000),
        billboard: {
          image: makeThreatMarker(threat.color),
          scale: threat.scale ?? 1,
          verticalOrigin: Cesium.VerticalOrigin.CENTER,
          heightReference: Cesium.HeightReference.NONE,
        },
      })
    })

    const target = Cesium.Cartesian3.fromDegrees(0, 40, 0)
    let heading = Cesium.Math.toRadians(0)
    const pitch = Cesium.Math.toRadians(-14)
    const range = 16800000

    const handleTick = () => {
      heading += 0.0018
      camera.lookAt(target, new Cesium.HeadingPitchRange(heading, pitch, range))
    }

    camera.lookAt(target, new Cesium.HeadingPitchRange(heading, pitch, range))

    viewer.clock.onTick.addEventListener(handleTick)

    return () => {
      viewer.clock.onTick.removeEventListener(handleTick)
      camera.lookAtTransform(Cesium.Matrix4.IDENTITY)
      viewerRef.current = null
      viewer.destroy()
    }
  }, [])

  return (
    <div className="landing-hero-globe-viewport">
      <div className="landing-hero-globe-stage">
        <div ref={hostRef} className="landing-hero-globe-canvas" />
      </div>
    </div>
  )
}

function awaitableProvider<T>(provider: T): T {
  return provider
}
